import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { TextField } from "@/components/form/TextField";
import { useAutosaveForm } from "@/components/form/useAutosaveForm";
import { SaveStatus } from "@/components/SaveStatus";

import { actionPlanKeys } from "../api/keys";
import { patchAction } from "../api/hooks";
import {
  ACTION_STATUSES,
  type ActionPlanAction,
  type ActionPlanDetail,
  type ActionStatus,
} from "../api/types";
import { patchPlanActions } from "../lib/selection";
import { NumberField } from "./fields/NumberField";
import { SelectField } from "./fields/SelectField";
import { TextareaField } from "./fields/TextareaField";

/**
 * The fields this editor actually writes.
 *
 * `status` is only editable in "full" mode, but the draft still carries the
 * value captured at mount — sending it back on every autosave would replay a
 * stale status over whatever the Track step (or another tab) has since set, and
 * the server routes any status in the payload through
 * `set_status_with_propagation`, cascading that revert to the questionnaire
 * answer and to the same action in the member's other plans.
 */
function toPatch(data: TrackingDraft, mode: "full" | "planning") {
  const { status, ...rest } = data;
  return {
    ...rest,
    ...(mode === "full" ? { status } : {}),
    start_date: data.start_date || null,
    deadline: data.deadline || null,
    responsible_name: data.responsible_name || null,
    target: data.target || null,
  };
}

interface TrackingDraft {
  status: ActionStatus;
  responsible_name: string;
  start_date: string;
  deadline: string;
  estimated_cost: number | null;
  estimated_cost_hours: number | null;
  target: string;
  comment: string;
}

/**
 * Inline tracking editor for a selected action (the visualization step): status,
 * owner, dates, cost and comment. Every field autosaves on change with a visible
 * <SaveStatus> — this is the on-change form that replaces the legacy submit-only
 * visualization page. A status change alters the plan's done count, so a save
 * invalidates the list.
 */
export function ActionEditor({
  action,
  planId,
  mode = "full",
}: {
  action: ActionPlanAction;
  /** Owning plan — the cache key this editor keeps up to date as it saves. */
  planId: string;
  /** "planning" hides the status field — status lives in the Track step. */
  mode?: "full" | "planning";
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { form, autosave } = useAutosaveForm<TrackingDraft>({
    defaultValues: {
      status: action.status ?? "not_done",
      responsible_name: action.responsible_name ?? "",
      start_date: action.start_date ?? "",
      deadline: action.deadline ?? "",
      estimated_cost: action.estimated_cost ?? null,
      estimated_cost_hours: action.estimated_cost_hours ?? null,
      target: action.target ?? "",
      comment: action.comment ?? "",
    },
    autosave: {
      // Empty date strings must serialize as null, not "" (the API rejects "").
      save: (data, signal) => patchAction(action.id, toPatch(data, mode), signal),
      onSaved: (data) => {
        void qc.invalidateQueries({ queryKey: actionPlanKeys.lists() });
        // Write the saved values straight into the cached plan. Everything
        // derived from it — the step-4 nav's "planned" tallies, the track
        // step's counts — is stale until this happens, and refetching the whole
        // plan on every debounced keystroke would be both wasteful and
        // disruptive to the form being typed in.
        qc.setQueryData<ActionPlanDetail>(
          actionPlanKeys.detail(planId),
          (plan) =>
            // Mirror exactly what was sent — writing the untouched `status`
            // here would stamp a stale value into every count derived from it.
            plan ? patchPlanActions(plan, [action.id], toPatch(data, mode)) : plan,
        );
      },
    },
  });

  const statusOptions = ACTION_STATUSES.map((s) => ({
    value: s,
    label: t(`actionPlans.status.${s}`),
  }));

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-4"
    >
      <div className="flex items-center justify-end">
        <SaveStatus status={autosave.status} />
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        {mode === "full" && (
          <form.Field name="status">
            {(field) => (
              <SelectField
                field={field}
                label={t("actionPlans.visualization.status")}
                options={statusOptions}
              />
            )}
          </form.Field>
        )}
        <form.Field name="responsible_name">
          {(field) => (
            <TextField
              field={field}
              label={t("actionPlans.visualization.responsible")}
              placeholder={t("actionPlans.visualization.responsiblePlaceholder")}
            />
          )}
        </form.Field>
        <form.Field name="start_date">
          {(field) => (
            <TextField
              field={field}
              type="date"
              label={t("actionPlans.visualization.startDate")}
            />
          )}
        </form.Field>
        <form.Field name="deadline">
          {(field) => (
            <TextField
              field={field}
              type="date"
              label={t("actionPlans.visualization.deadline")}
            />
          )}
        </form.Field>
        <form.Field name="estimated_cost">
          {(field) => (
            <NumberField
              field={field}
              label={t("actionPlans.visualization.estimatedCost")}
            />
          )}
        </form.Field>
        <form.Field name="estimated_cost_hours">
          {(field) => (
            <NumberField
              field={field}
              label={t("actionPlans.visualization.estimatedCostHours")}
            />
          )}
        </form.Field>
      </div>

      <form.Field name="target">
        {(field) => (
          <TextField
            field={field}
            label={t("actionPlans.visualization.target")}
            placeholder={t("actionPlans.visualization.targetPlaceholder")}
          />
        )}
      </form.Field>
      <form.Field name="comment">
        {(field) => (
          <TextareaField
            field={field}
            label={t("actionPlans.visualization.comment")}
            placeholder={t("actionPlans.visualization.commentPlaceholder")}
          />
        )}
      </form.Field>
    </form>
  );
}
